import type { StateCreator } from "zustand";
import type { KeyMessageSlice } from "./key-message.slice";

type KeyMessageActions = Pick<
  KeyMessageSlice,
  | "setCreateKeyMessageGroupModalOpen"
  | "setSaveSearchKeyMessageModalOpen"
  | "setCreateKeyMessageModalOpen"
  | "setKeyMessageName"
  | "setSearchTerm"
  | "setIsAdvancedSearch"
  | "setSelectedGroup"
  | "setIsEditingKeyMessageGroup"
  | "setKeyMessageGroupName"
  | "setIsEditingKeyMessage"
  | "setEditingKeyMessageId"
  | "setInsightsKeyMessageId"
  | "setSelectedGroups"
  | "setDeleteKeyMessageOrGroupModalOpen"
  | "setIsDeletingKeyMessageAndGroup"
  | "setKeyMessageInsightsModalOpen"
  | "updateSelectedKeyMessageFilterExclusion"
  | "resetSaveSearchKeyMessageModal"
  | "resetCreateKeyMessageModal"
  | "resetDeleteKeyMessageOrGroupModal"
>;

export const createKeyMessageActions: StateCreator<
  KeyMessageSlice,
  [["zustand/devtools", never]],
  [],
  KeyMessageActions
> = (set) => ({
  setCreateKeyMessageGroupModalOpen: (isOpen: boolean) =>
    set(
      { isCreateKeyMessageGroupModalOpen: isOpen },
      false,
      "[User] KeyMessage Group: toggle key message group modal",
    ),

  setSaveSearchKeyMessageModalOpen: (isOpen: boolean) =>
    set(
      { isSaveSearchKeyMessageModalOpen: isOpen },
      false,
      "[User] KeyMessage: Toggle set save key message modal",
    ),

  setCreateKeyMessageModalOpen: (isOpen: boolean) =>
    set(
      { isCreateKeyMessageModalOpen: isOpen },
      false,
      "[User] KeyMessage: Toggle create key message modal",
    ),

  setKeyMessageName: (name: string) =>
    set(
      { keyMessageName: name },
      false,
      "[User] KeyMessage: Set key message name",
    ),

  setSearchTerm: (term: string) =>
    set({ searchTerm: term }, false, "[User] KeyMessage: Set search term"),

  setIsAdvancedSearch: (isAdvancedSearch: boolean) =>
    set(
      { isAdvancedSearch },
      false,
      "[User] KeyMessage: Set is advanced search",
    ),

  setSelectedGroup: (group: { name: string; id: string } | null) =>
    set(
      { selectedGroup: group },
      false,
      "[User] KeyMessage: Set selected group",
    ),

  setIsEditingKeyMessageGroup: (isEditing: boolean) =>
    set(
      { isEditingKeyMessageGroup: isEditing },
      false,
      "[User] KeyMessage: Set is editing key message group",
    ),

  setKeyMessageGroupName: (name: string) =>
    set(
      { keyMessageGroupName: name },
      false,
      "[User] KeyMessage: Set key message group name",
    ),

  setIsEditingKeyMessage: (isEditing: boolean) =>
    set(
      { isEditingKeyMessage: isEditing },
      false,
      "[User] KeyMessage: Set is editing key message",
    ),

  setEditingKeyMessageId: (id: string | null) =>
    set(
      { editingKeyMessageId: id },
      false,
      "[User] KeyMessage: Set editing key message id",
    ),

  setInsightsKeyMessageId: (id: string | null) =>
    set(
      { insightsKeyMessageId: id },
      false,
      "[User] KeyMessage: Set insights key message id",
    ),

  setSelectedGroups: (groups: Set<string>) =>
    set(
      { selectedGroups: groups },
      false,
      "[User] KeyMessage: Set selected groups",
    ),

  setDeleteKeyMessageOrGroupModalOpen: (isOpen: boolean) =>
    set(
      { isDeleteKeyMessageOrGroupModalOpen: isOpen },
      false,
      "[User] KeyMessage: Toggle delete key message modal",
    ),

  setIsDeletingKeyMessageAndGroup: (type: "group" | "message" | null) =>
    set(
      { isDeletingKeyMessageAndGroup: type },
      false,
      "[User] KeyMessage: Set deleting key message type",
    ),

  setKeyMessageInsightsModalOpen: (isOpen: boolean) =>
    set(
      { isKeyMessageInsightsModalOpen: isOpen },
      false,
      "[User] KeyMessage: Toggle key message insights modal",
    ),

  updateSelectedKeyMessageFilterExclusion: (
    selectedKeyMessageFilterExclusion,
  ) =>
    set(
      { selectedKeyMessageFilterExclusion },
      false,
      "[User] KeyMessage: Update selected key message filter exclusion",
    ),

  resetSaveSearchKeyMessageModal: () =>
    set(
      {
        isSaveSearchKeyMessageModalOpen: false,
        isCreateKeyMessageGroupModalOpen: false,
        keyMessageName: "",
        selectedGroup: null,
        searchTerm: "",
      },
      false,
      "[User] KeyMessage: Reset save search key message modal",
    ),

  resetCreateKeyMessageModal: () =>
    set(
      {
        isCreateKeyMessageModalOpen: false,
        isCreateKeyMessageGroupModalOpen: false,
        keyMessageName: "",
        searchTerm: "",
        selectedGroup: null,
        editingKeyMessageId: null,
        isEditingKeyMessage: false,
      },
      false,
      "[User] KeyMessage: Reset create key message modal",
    ),

  resetDeleteKeyMessageOrGroupModal: () =>
    set(
      {
        isDeleteKeyMessageOrGroupModalOpen: false,
        isDeletingKeyMessageAndGroup: null,
        isDeletingKeyMessage: false,
        isDeletingKeyMessageGroup: false,
        keyMessageName: "",
        selectedGroup: null,
        editingKeyMessageId: null,
      },
      false,
      "[User] KeyMessage: Reset delete key message or group modal",
    ),
});
